import { Interview } from "./Interview";
import { Activity } from "./Activity";
import { fileAsText } from "../utils/fileUtils";
import { saveAs } from "file-saver";

interface SavedInterview {
  interview: Interview;
  activities: Activity[];
}

export function saveInterview(savedInterview: SavedInterview) {
  const asString = JSON.stringify(savedInterview, null, 2);
  const blob = new Blob([asString], { type: "application/json" });
  saveAs(blob, "interview.json");
}

export async function loadInterview(
  input: HTMLInputElement
): Promise<SavedInterview> {
  try {
    const text = await fileAsText(input);
    const savedInterview = await validateSavedInterview(text);
    return savedInterview;
  } catch (error) {
    console.error(error);
    throw new Error("Could not load saved interview.");
  }
}

async function validateSavedInterview(
  savedInterviewString: string
): Promise<SavedInterview> {
  try {
    const savedInterview = JSON.parse(savedInterviewString) as SavedInterview;
    if (!savedInterview.interview || !savedInterview.activities) {
      throw new Error(
        "Could not find interview information in the loaded file."
      );
    }

    validateInterview(savedInterview.interview);
    validateActivities(savedInterview.activities);
    return savedInterview;
  } catch (error) {
    console.error(error);
    throw new Error(`Could not validate saved interview.`);
  }
}

function validateInterview(object: any): Promise<Interview> {
  return new Promise((resolve, reject) => {
    try {
      const interview = object as Interview;

      if (typeof interview.isMultiInterview !== "boolean") {
        reject("isMultiInterview is not present");
        return;
      }

      if (!Array.isArray(interview.topics)) {
        reject("topics are not present");
        return;
      }

      if (!Array.isArray(interview.resultValues)) {
        reject("resultValues are not present");
        return;
      }

      if (typeof interview.resultPanels !== "object") {
        reject("resultPanels are not present");
        return;
      }

      resolve(interview);
    } catch (error) {
      reject(error);
    }
  });
}

function validateActivities(object: any): Promise<Activity[]> {
  return new Promise((resolve, reject) => {
    try {
      if (!Array.isArray(object)) {
        reject("Activities are not an array");
        return;
      }

      const activities = object as Activity[];

      for (const activity of activities) {
        if (
          typeof activity.id !== "string" ||
          typeof activity.article !== "string" ||
          typeof activity.name !== "string" ||
          typeof activity.comments !== "string"
        ) {
          reject("Could not parse activity");
          return;
        }
      }

      resolve(activities);
    } catch (error) {
      reject(error);
    }
  });
}
